import { useEffect, useRef } from 'react'

// =============================================================================
// LandingOrb — canvas-based persona symbol
// =============================================================================
// Design reference: docs/25_ORB_DESIGN.md
//
// Interactivity (new, per-persona — see useLandingPersona.ts):
//   • Parallax: orb translates toward the cursor by N px, eased
//   • Magnetic node pull: nodes within R px of cursor are tugged toward it
//   • Hover boost: intensity + dash speed bumped while hovered
//   • Click ripple: clicking spawns an expanding ring at the click point
//   • Idle motion: dashed outer ring spins on its own; whole orb "breathes"
// =============================================================================

interface OrbInteractionParams {
  parallaxMax: number
  parallaxEase: number
  hoverIntensity: number
  magneticRadius: number
  magneticStrength: number
  dashSpinSpeed: number
  breatheAmplitude: number
  breatheFrequency: number
  rippleSpeed: number
  cursor: 'default' | 'pointer' | 'crosshair' | 'grab'
}

interface LandingOrbProps {
  personaId: 'jarvis' | 'ultron' | 'ares' | 'symbiote' | 'friday'
  primary: string
  secondary: string
  glow: string
  shape: 'circle' | 'triangle' | 'triangle-inverted'
  radiusScale: number
  rings: number
  nodeCount: number
  size?: number
  intensity?: number // 0..1 — drives node opacity + connection density
  interaction?: OrbInteractionParams
  /** When false, mouse handlers are skipped — useful for tiny preview thumbs. */
  interactive?: boolean
}

const DEFAULT_INTERACTION: OrbInteractionParams = {
  parallaxMax: 6,
  parallaxEase: 0.06,
  hoverIntensity: 1.25,
  magneticRadius: 80,
  magneticStrength: 0.35,
  dashSpinSpeed: 0.35,
  breatheAmplitude: 1.5,
  breatheFrequency: 0.012,
  rippleSpeed: 1.0,
  cursor: 'default',
}

function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

function pointsOnTriangle(n: number, cx: number, cy: number, r: number): Array<[number, number]> {
  const top: [number, number] = [cx, cy - r * 1.15]
  const bl: [number, number] = [cx - r * 1.0, cy + r * 0.6]
  const br: [number, number] = [cx + r * 1.0, cy + r * 0.6]
  const edges: Array<[[number, number], [number, number]]> = [
    [top, br],
    [br, bl],
    [bl, top],
  ]
  const perimeter = edges.reduce(
    (acc, [a, b]) => acc + Math.hypot(b[0] - a[0], b[1] - a[1]),
    0,
  )
  const points: Array<[number, number]> = []
  let filled = 0
  for (let e = 0; e < 3; e++) {
    const [a, b] = edges[e]
    const edgeLen = Math.hypot(b[0] - a[0], b[1] - a[1])
    const count = e < 2 ? Math.round((edgeLen / perimeter) * n) : n - filled
    for (let i = 0; i < count; i++) {
      const t = i / Math.max(1, count - 1)
      points.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
    filled += count
  }
  return points
}

/**
 * Distribute N points evenly along the edges of an inverted triangle (▽).
 * Apex at bottom-center, base at the top. Used for Symbiote's V-shape orb.
 */
function pointsOnInvertedTriangle(
  n: number,
  cx: number,
  cy: number,
  r: number,
): Array<[number, number]> {
  const tl: [number, number] = [cx - r * 1.0, cy - r * 0.6]
  const tr: [number, number] = [cx + r * 1.0, cy - r * 0.6]
  const bot: [number, number] = [cx, cy + r * 1.15]
  const edges: Array<[[number, number], [number, number]]> = [
    [tl, tr],
    [tr, bot],
    [bot, tl],
  ]
  const perimeter = edges.reduce(
    (acc, [a, b]) => acc + Math.hypot(b[0] - a[0], b[1] - a[1]),
    0,
  )
  const points: Array<[number, number]> = []
  let filled = 0
  for (let e = 0; e < 3; e++) {
    const [a, b] = edges[e]
    const edgeLen = Math.hypot(b[0] - a[0], b[1] - a[1])
    const count = e < 2 ? Math.round((edgeLen / perimeter) * n) : n - filled
    for (let i = 0; i < count; i++) {
      const t = i / Math.max(1, count - 1)
      points.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
    }
    filled += count
  }
  return points
}

interface Ripple {
  x: number
  y: number
  age: number
}

export default function LandingOrb({
  personaId,
  primary,
  secondary,
  glow,
  shape,
  radiusScale,
  rings,
  nodeCount,
  size = 480,
  intensity = 1,
  interaction = DEFAULT_INTERACTION,
  interactive = true,
}: LandingOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(0)
  const visibleRef = useRef<boolean>(true)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    const dpr = window.devicePixelRatio || 1
    const W = size
    const H = size
    canvas.width = W * dpr
    canvas.height = H * dpr
    canvas.style.width = `${W}px`
    canvas.style.height = `${H}px`
    canvas.style.cursor = interactive ? interaction.cursor : 'default'
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    const cx = W / 2
    const cy = H / 2
    const baseRadius = W * 0.34 * radiusScale

    const [pr, pg, pb] = hexToRgb(primary)
    const [sr, sg, sb] = hexToRgb(secondary)

    const palette = [
      `rgba(${pr},${pg},${pb},__A__)`,
      `rgba(${pr + 30},${pg + 30},${pb + 30},__A__)`,
      `rgba(${sr},${sg},${sb},__A__)`,
      `rgba(255,255,255,__A__)`,
      `rgba(${pr},${pg},${pb},__A__)`,
      `rgba(${sr + 20},${sg + 20},${sb + 20},__A__)`,
    ]

    interface Node {
      x: number
      y: number
      vx: number
      vy: number
      tx: number
      ty: number
      ring: number
      colorBase: string
      size: number
      alpha: number
      twinkle: number
    }
    const nodes: Node[] = []

    let outerTargets: Array<[number, number]> = []
    if (shape === 'triangle') {
      outerTargets = pointsOnTriangle(Math.round(nodeCount * 0.5), cx, cy, baseRadius * 0.95)
    } else if (shape === 'triangle-inverted') {
      outerTargets = pointsOnInvertedTriangle(Math.round(nodeCount * 0.5), cx, cy, baseRadius * 0.95)
    }

    let outerIdx = 0
    const perRing = Math.ceil(nodeCount / rings)
    for (let i = 0; i < nodeCount; i++) {
      const ring = Math.min(rings - 1, Math.floor(i / perRing))
      const ringFraction = (ring + 1) / rings
      const ringRadius = baseRadius * (0.25 + ringFraction * 0.72)
      let tx: number, ty: number
      if ((shape === 'triangle' || shape === 'triangle-inverted') && ring === rings - 1 && outerIdx < outerTargets.length) {
        const [px, py] = outerTargets[outerIdx++]
        tx = px
        ty = py
      } else {
        const angle = (i / nodeCount) * Math.PI * 2 + ring * 0.9 + Math.random() * 0.3
        const jitter = ringRadius * 0.06
        tx = cx + Math.cos(angle) * ringRadius + (Math.random() - 0.5) * jitter
        ty = cy + Math.sin(angle) * ringRadius + (Math.random() - 0.5) * jitter
      }
      const startAngle = Math.random() * Math.PI * 2
      const startR = Math.random() * baseRadius * 0.25
      nodes.push({
        x: cx + Math.cos(startAngle) * startR,
        y: cy + Math.sin(startAngle) * startR,
        vx: 0,
        vy: 0,
        tx,
        ty,
        ring,
        colorBase: palette[i % palette.length],
        size: ring === 0 ? 3.2 : ring === rings - 1 ? 1.6 : 2.2,
        alpha: 0,
        twinkle: Math.random() * Math.PI * 2,
      })
    }

    // -----------------------------------------------------------------------
    // Interactive state (refs survive between frames without triggering re-render)
    // -----------------------------------------------------------------------
    const mouse = { x: cx, y: cy, isOver: false }
    const parallax = { x: 0, y: 0 }
    let hoverBoost = 1.0 // smoothed toward 1.0 (no hover) or `interaction.hoverIntensity`
    let dashOffset = 0
    const ripples: Ripple[] = []

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouse.x = e.clientX - rect.left
      mouse.y = e.clientY - rect.top
      mouse.isOver = true
    }
    const onLeave = () => {
      mouse.isOver = false
    }
    const onClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      ripples.push({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        age: 0,
      })
      // Cap the ripple count so a button-mashing user doesn't tank the framerate.
      if (ripples.length > 6) ripples.shift()
    }

    if (interactive) {
      canvas.addEventListener('mousemove', onMove, { passive: true })
      canvas.addEventListener('mouseleave', onLeave, { passive: true })
      canvas.addEventListener('click', onClick)
    }

    // IntersectionObserver — pause when off-screen
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          visibleRef.current = e.isIntersecting
        }
      },
      { threshold: 0.05 },
    )
    observer.observe(canvas)

    let tick = 0
    function draw() {
      animRef.current = requestAnimationFrame(draw)
      if (!visibleRef.current) return
      tick++
      ctx.clearRect(0, 0, W, H)

      // --- Interaction state updates --------------------------------------
      // Parallax target: direction from center to cursor, clamped to parallaxMax
      let targetPX = 0
      let targetPY = 0
      if (interactive && mouse.isOver) {
        const dx = mouse.x - cx
        const dy = mouse.y - cy
        const dist = Math.hypot(dx, dy)
        if (dist > 0) {
          const reach = Math.min(1, dist / baseRadius) // ramp up over the orb's radius
          targetPX = (dx / dist) * interaction.parallaxMax * reach
          targetPY = (dy / dist) * interaction.parallaxMax * reach
        }
      }
      parallax.x += (targetPX - parallax.x) * interaction.parallaxEase
      parallax.y += (targetPY - parallax.y) * interaction.parallaxEase

      // Hover boost smoothed (eases in/out over ~12 frames)
      const targetBoost = mouse.isOver ? interaction.hoverIntensity : 1.0
      hoverBoost += (targetBoost - hoverBoost) * 0.12

      // Dash offset advances over time, faster when hovered
      dashOffset += interaction.dashSpinSpeed * (mouse.isOver ? 1.8 : 1.0)

      // Effective intensity (state intensity × hover boost)
      const effectiveIntensity = intensity * hoverBoost

      // Idle breathe — applied as canvas-wide scale
      const breathe =
        1 +
        Math.sin(tick * interaction.breatheFrequency) *
          (interaction.breatheAmplitude / 100)

      // Apply parallax + breathe via context transform (saved/restored each frame)
      ctx.save()
      ctx.translate(cx + parallax.x, cy + parallax.y)
      ctx.scale(breathe, breathe)
      ctx.translate(-cx, -cy)

      // --- Background glow pulse ------------------------------------------
      const pulse = 0.5 + 0.5 * Math.sin(tick * 0.018)
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, baseRadius * 1.6)
      grad.addColorStop(0, glow.replace(/[\d.]+\)/, `${0.18 + pulse * 0.12})`))
      grad.addColorStop(0.45, glow.replace(/[\d.]+\)/, `${0.06 + pulse * 0.05})`))
      grad.addColorStop(1, 'transparent')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, W, H)

      // --- Rings ----------------------------------------------------------
      for (let r = 0; r < rings; r++) {
        const rr = baseRadius * (0.25 + ((r + 1) / rings) * 0.72)
        const ringAlpha = 0.08 + (r / rings) * 0.18
        const dash = r === rings - 1
        const isOuter = r === rings - 1

        ctx.strokeStyle =
          primary +
          Math.round((ringAlpha + (isOuter ? pulse * 0.15 : 0)) * 255)
            .toString(16)
            .padStart(2, '0')
        ctx.lineWidth = isOuter ? 1 : 0.5
        if (dash) {
          ctx.setLineDash([4, 8])
          ctx.lineDashOffset = -dashOffset
        } else {
          ctx.setLineDash([])
          ctx.lineDashOffset = 0
        }

        if (shape === 'triangle' && r === rings - 1) {
          ctx.beginPath()
          ctx.moveTo(cx, cy - rr * 1.15)
          ctx.lineTo(cx + rr * 1.0, cy + rr * 0.6)
          ctx.lineTo(cx - rr * 1.0, cy + rr * 0.6)
          ctx.closePath()
          ctx.stroke()
        } else if (shape === 'triangle-inverted' && r === rings - 1) {
          ctx.beginPath()
          ctx.moveTo(cx - rr * 1.0, cy - rr * 0.6)
          ctx.lineTo(cx + rr * 1.0, cy - rr * 0.6)
          ctx.lineTo(cx, cy + rr * 1.15)
          ctx.closePath()
          ctx.stroke()
        } else {
          ctx.beginPath()
          ctx.arc(cx, cy, rr, 0, Math.PI * 2)
          ctx.stroke()
        }
      }
      ctx.setLineDash([])
      ctx.lineDashOffset = 0

      // --- Nodes ----------------------------------------------------------
      // Cursor position in the orb's local (post-parallax) coordinate frame so
      // magnetism stays accurate even while the orb is translated.
      const mouseLocalX = mouse.x - parallax.x
      const mouseLocalY = mouse.y - parallax.y
      const magRadius = interaction.magneticRadius
      const magStrength = interaction.magneticStrength
      const useMagnet = interactive && mouse.isOver

      for (const n of nodes) {
        // Standard target pull
        n.vx += (n.tx - n.x) * 0.014
        n.vy += (n.ty - n.y) * 0.014

        // Magnetic pull from cursor — falls off linearly with distance.
        if (useMagnet) {
          const mdx = mouseLocalX - n.x
          const mdy = mouseLocalY - n.y
          const mdist = Math.hypot(mdx, mdy)
          if (mdist > 0 && mdist < magRadius) {
            const pull = (1 - mdist / magRadius) * magStrength
            n.vx += (mdx / mdist) * pull
            n.vy += (mdy / mdist) * pull
          }
        }

        // Brownian jitter + damping (same as before)
        n.vx += (Math.random() - 0.5) * 0.06
        n.vy += (Math.random() - 0.5) * 0.06
        n.vx *= 0.85
        n.vy *= 0.85
        n.x += n.vx
        n.y += n.vy
        n.alpha = Math.min(1, n.alpha + 0.012)
        n.twinkle += 0.04
        const twinkle = 0.6 + 0.4 * Math.sin(n.twinkle)
        const a = n.alpha * twinkle * effectiveIntensity

        ctx.beginPath()
        ctx.arc(n.x, n.y, n.size, 0, Math.PI * 2)
        ctx.fillStyle = n.colorBase.replace('__A__', a.toFixed(3))
        ctx.fill()
      }

      // --- Connection strands --------------------------------------------
      const linkAlpha = 0.18 * effectiveIntensity
      const linkDist = 52 + (mouse.isOver ? 18 : 0) // hover widens the lattice
      ctx.lineWidth = 0.4
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < Math.min(i + 6, nodes.length); j++) {
          const a = nodes[i]
          const b = nodes[j]
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.hypot(dx, dy)
          if (dist < linkDist) {
            const al = (1 - dist / linkDist) * linkAlpha
            ctx.strokeStyle =
              primary +
              Math.round(al * 255)
                .toString(16)
                .padStart(2, '0')
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }
      }

      // --- Center reactor ------------------------------------------------
      const reactorR = 22 + pulse * 4
      const cGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, reactorR)
      cGrad.addColorStop(0, primary + 'ee')
      cGrad.addColorStop(0.6, primary + '55')
      cGrad.addColorStop(1, 'transparent')
      ctx.fillStyle = cGrad
      ctx.beginPath()
      ctx.arc(cx, cy, reactorR, 0, Math.PI * 2)
      ctx.fill()

      // White hot center
      ctx.fillStyle = 'rgba(255,255,255,0.9)'
      ctx.beginPath()
      ctx.arc(cx, cy, 3.5, 0, Math.PI * 2)
      ctx.fill()

      // --- Click ripples (rendered in local frame) -----------------------
      for (let i = ripples.length - 1; i >= 0; i--) {
        const ripple = ripples[i]
        ripple.age += interaction.rippleSpeed
        const t = ripple.age / 60 // ~1s lifetime at 60fps × rippleSpeed
        if (t > 1) {
          ripples.splice(i, 1)
          continue
        }
        // Ease-out: fast at the start, slow at the edge
        const eased = 1 - Math.pow(1 - t, 2)
        const radius = baseRadius * 1.6 * eased
        const alpha = (1 - t) * 0.7
        ctx.strokeStyle =
          primary +
          Math.round(alpha * 255)
            .toString(16)
            .padStart(2, '0')
        ctx.lineWidth = 2 * (1 - t * 0.7)
        ctx.beginPath()
        ctx.arc(ripple.x - parallax.x, ripple.y - parallax.y, radius, 0, Math.PI * 2)
        ctx.stroke()

        // Inner secondary-color ripple for extra punch on Ares
        if (radius > 30) {
          ctx.strokeStyle =
            secondary +
            Math.round(alpha * 0.5 * 255)
              .toString(16)
              .padStart(2, '0')
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.arc(ripple.x - parallax.x, ripple.y - parallax.y, radius * 0.7, 0, Math.PI * 2)
          ctx.stroke()
        }
      }

      ctx.restore()
    }

    draw()
    return () => {
      cancelAnimationFrame(animRef.current)
      observer.disconnect()
      if (interactive) {
        canvas.removeEventListener('mousemove', onMove)
        canvas.removeEventListener('mouseleave', onLeave)
        canvas.removeEventListener('click', onClick)
      }
    }
  }, [
    personaId,
    primary,
    secondary,
    glow,
    shape,
    radiusScale,
    rings,
    nodeCount,
    size,
    intensity,
    interaction,
    interactive,
  ])

  return <canvas ref={canvasRef} style={{ display: 'block' }} aria-hidden="true" />
}
